import React, { createContext, useState, useContext, useEffect } from 'react';
import { fetchWithNoCache } from './fetchUtils';

// Create the auth context
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Check authentication status when the component mounts
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await fetchWithNoCache('/api/check-auth', {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsAuthenticated(true);
          setUser({
            username: data.username,
            locations: data.locations || []
          });
        } else {
          // Handle unauthenticated state
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login function
  const login = async (credentials) => {
    setIsLoading(true);
    try {
      const response = await fetchWithNoCache('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(credentials)
      });

      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setUser({
          username: credentials.emailOrUsername,
          locations: data.locations || []
        });
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error occurred' };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    setIsLoading(true);
    try {
      await fetchWithNoCache('/api/logout', {
        method: 'POST',
        credentials: 'include'
      });
      
      // Clear auth state regardless of server response
      setIsAuthenticated(false);
      setUser(null);
      
      // Force clear any local storage related to authentication
      localStorage.removeItem('fluencyUserSettings');
      sessionStorage.clear();
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      // Still consider logged out on client side even if server request fails
      setIsAuthenticated(false);
      setUser(null);
      return { success: false, error: 'Logout failed on server, but logged out locally' };
    } finally {
      setIsLoading(false);
    }
  };

  // Expose the auth context
  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;